import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { commissionsService } from '../../services';
import CommissionForm from './CommissionForm';
import TaxForms from './TaxForms/TaxForms';
import CommissionMessagingModal from './CommissionMessagingModal';

// Voyager Pro reskin (Figma CommissionsPage) - kept as a table+modals layout
// (not the list+drawer pattern used elsewhere) since Figma's own Commissions
// design is a data-table with KPI cards, matching the density this feature
// needs. Preserves all real business logic (real 8-status taxonomy, richer
// than Figma's 5-status sample data; agency vs independent-agent column/
// action differences; draft-edit-vs-view branching).
const STATUS_CFG = {
  approved: { label: 'Approved', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  paid: { label: 'Paid', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  pending: { label: 'Pending', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  denied: { label: 'Denied', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  on_hold: { label: 'On Hold', bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
  needs_reply: { label: 'Needs Reply', bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  needs_agent_reply: { label: 'Needs Agent Reply', bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  needs_admin_reply: { label: 'Needs Admin Reply', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  draft: { label: 'Draft', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
  // 'under_review' is the real commissions.status ENUM value independent agents'
  // "Processing" option maps to (see backend/routes/commissions.js for why plain
  // 'processing' is not a valid enum value).
  under_review: { label: 'Processing', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

const CommissionsList = () => {
  const { user } = useAuth();
  const isIndependentAgent = user?.role === 'independent_agent';
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showTaxForms, setShowTaxForms] = useState(false);
  const [showMessagingModal, setShowMessagingModal] = useState(false);
  const [selectedCommissionForMessaging, setSelectedCommissionForMessaging] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCommissions();
  }, []);

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      const data = await commissionsService.getAll();
      setCommissions(data);
    } catch (error) {
      console.error('Error fetching commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCommissionSubmit = () => {
    fetchCommissions();
    setShowForm(false);
  };

  const handleMessageSent = () => {
    fetchCommissions();
    setShowMessagingModal(false);
    setSelectedCommissionForMessaging(null);
  };

  const handleStatusUpdate = async (commissionId, newStatus) => {
    try {
      setError('');
      await commissionsService.updateOwnStatus(commissionId, newStatus);
      await fetchCommissions();
    } catch (error) {
      console.error('Failed to update status:', error);
      setError(error.response?.data?.error || 'Failed to update commission status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const datePart = dateString.split(/[T ]/)[0];
    const [year, month, day] = datePart.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const filteredCommissions = commissions.filter(commission => {
    if (filter !== 'all' && commission.status !== filter) return false;

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        commission.reservation_number?.toLowerCase().includes(search) ||
        commission.guest_first_name?.toLowerCase().includes(search) ||
        commission.guest_last_name?.toLowerCase().includes(search) ||
        commission.supplier?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    if (dateRange.start && new Date(commission.travel_date) < new Date(dateRange.start)) return false;
    if (dateRange.end && new Date(commission.travel_date) > new Date(dateRange.end)) return false;

    return true;
  });

  const stats = {
    total: commissions.length,
    pending: commissions.filter(c => c.status === 'pending').length,
    paid: commissions.filter(c => c.status === 'paid').length,
    totalAgent: commissions.reduce((sum, c) => sum + parseFloat(c.agent_commission || 0), 0),
    paidAgent: commissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + parseFloat(c.agent_commission || 0), 0),
    pendingAgent: commissions.filter(c => c.status === 'pending').reduce((sum, c) => sum + parseFloat(c.agent_commission || 0), 0),
  };

  const statusBreakdown = ['paid', 'pending', 'denied', 'on_hold', 'needs_agent_reply', 'draft']
    .map(s => ({ status: s, count: commissions.filter(c => c.status === s).length }))
    .filter(s => s.count > 0);

  const KPI_CARDS = [
    {
      label: 'Total Commissions', value: stats.total.toString(), sub: 'all time',
      iconBg: 'bg-blue-50', iconColor: 'text-blue-600',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />,
    },
    {
      label: 'Pending Approval', value: stats.pending.toString(),
      sub: stats.pendingAgent > 0 ? `${formatCurrency(stats.pendingAgent)} pending` : 'none pending',
      iconBg: 'bg-amber-50', iconColor: 'text-amber-600',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />,
    },
    {
      label: 'Paid', value: stats.paid.toString(), sub: 'commissions received',
      iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />,
    },
    {
      label: 'Paid Commissions', value: formatCurrency(stats.paidAgent), sub: 'total earned',
      iconBg: 'bg-purple-50', iconColor: 'text-purple-600',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
    },
  ];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 text-[var(--muted-foreground)]">
        <div className="w-12 h-12 border-4 border-[var(--border)] rounded-full animate-spin" style={{ borderTopColor: 'var(--primary)' }} />
        <p className="text-sm">Loading commissions...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 space-y-6">
      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-300 text-red-800 rounded-xl text-sm">
          {error}
        </div>
      )}

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-3xl text-[var(--foreground)]">
            Commission Management
          </h1>
          <p className="text-sm text-[var(--muted-foreground)] mt-1">
            {isIndependentAgent ? 'Track and manage your commissions' : 'Track and manage your commission requests'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isIndependentAgent && (
            <button
              onClick={() => setShowTaxForms(true)}
              className="flex items-center gap-2 px-4 py-2.5 border border-[var(--border)] rounded-xl text-sm font-semibold text-[var(--foreground)] hover:bg-[var(--muted)]/50 transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
              Tax Forms
            </button>
          )}
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[var(--primary)] text-white rounded-xl text-sm font-semibold hover:bg-[var(--primary)]/90 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            {isIndependentAgent ? 'Record Commission' : 'Submit New Commission'}
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {KPI_CARDS.map((card, i) => (
          <div key={i} className="bg-white border border-[var(--border)] rounded-2xl p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${card.iconBg} ${card.iconColor}`}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">{card.icon}</svg>
              </div>
              <div className="min-w-0">
                <div className="text-xs text-[var(--muted-foreground)] truncate">{card.label}</div>
                <div className="text-xl font-bold text-[var(--foreground)] mt-0.5">{card.value}</div>
              </div>
            </div>
            <div className="text-xs text-[var(--muted-foreground)]">{card.sub}</div>
          </div>
        ))}
      </div>

      {/* Status breakdown */}
      {statusBreakdown.length > 0 && (
        <div className="bg-white border border-[var(--border)] rounded-2xl p-5">
          <div className="text-sm font-semibold text-[var(--foreground)] mb-3">Status Breakdown</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
            {statusBreakdown.map(({ status, count }) => {
              const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
              const pct = stats.total > 0 ? (count / stats.total) * 100 : 0;
              return (
                <div key={status}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className={`font-medium ${cfg.text}`}>{cfg.label}</span>
                    <span className="text-[var(--muted-foreground)]">{count}</span>
                  </div>
                  <div className="h-1.5 bg-[var(--muted)] rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${cfg.dot}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-[var(--border)] rounded-2xl p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by reservation #, guest name, or supplier..."
              className="w-full pl-9 pr-4 py-2.5 border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] bg-[var(--muted)]/20 focus:border-[var(--accent)] outline-none transition-colors"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2.5 border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] bg-[var(--muted)]/20 focus:border-[var(--accent)] outline-none transition-colors appearance-none cursor-pointer min-w-[140px]"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            {isIndependentAgent && <option value="under_review">Processing</option>}
            <option value="paid">Paid</option>
            {!isIndependentAgent && (
              <>
                <option value="denied">Denied</option>
                <option value="on_hold">On Hold</option>
                <option value="needs_reply">Needs Reply</option>
                <option value="needs_agent_reply">Needs Agent Reply</option>
                <option value="needs_admin_reply">Needs Admin Reply</option>
              </>
            )}
          </select>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="px-3 py-2.5 border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] bg-[var(--muted)]/20 focus:border-[var(--accent)] outline-none transition-colors cursor-pointer"
            />
            <span className="text-[var(--muted-foreground)] text-sm">to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="px-3 py-2.5 border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] bg-[var(--muted)]/20 focus:border-[var(--accent)] outline-none transition-colors cursor-pointer"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--muted)]/30">
                {['Date', 'Reservation', 'Guest', 'Supplier', 'Travel', isIndependentAgent ? 'Amount' : 'Gross', ...(!isIndependentAgent ? ['Agent'] : []), 'Status', 'Actions'].map(col => (
                  <th key={col} className="px-4 py-3 text-left text-xs font-bold text-[var(--muted-foreground)] uppercase tracking-widest whitespace-nowrap">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {filteredCommissions.length === 0 ? (
                <tr>
                  <td colSpan={isIndependentAgent ? 8 : 9} className="px-4 py-12 text-center text-sm text-[var(--muted-foreground)]">
                    {commissions.length === 0 ? 'No commissions found' : 'No commissions match your filters'}
                  </td>
                </tr>
              ) : filteredCommissions.map(commission => (
                <tr key={commission.id} className="hover:bg-[var(--muted)]/20 transition-colors">
                  <td className="px-4 py-3 text-sm text-[var(--foreground)] whitespace-nowrap">{formatDate(commission.created_at)}</td>
                  <td className="px-4 py-3 text-sm font-medium text-[var(--foreground)] whitespace-nowrap">{commission.reservation_number}</td>
                  <td className="px-4 py-3 text-sm text-[var(--foreground)] whitespace-nowrap">{commission.guest_first_name} {commission.guest_last_name}</td>
                  <td className="px-4 py-3 text-sm text-[var(--foreground)]">{commission.supplier}</td>
                  <td className="px-4 py-3 text-sm text-[var(--foreground)] whitespace-nowrap">{formatDate(commission.travel_date)}</td>
                  <td className="px-4 py-3 text-sm text-[var(--foreground)] text-right whitespace-nowrap">{formatCurrency(commission.gross_commission)}</td>
                  {!isIndependentAgent && (
                    <td className="px-4 py-3 text-sm font-medium text-[var(--foreground)] text-right whitespace-nowrap">{formatCurrency(commission.agent_commission)}</td>
                  )}
                  <td className="px-4 py-3">
                    <span className="capitalize"><StatusBadge status={commission.status} /></span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => {
                          if (commission.status === 'draft') {
                            setSelectedCommission(commission);
                            setShowForm(true);
                          } else {
                            setSelectedCommission(commission);
                          }
                        }}
                        className="text-xs font-semibold text-[var(--primary)] hover:underline"
                      >
                        {commission.status === 'draft' ? 'Edit' : 'View'}
                      </button>
                      {isIndependentAgent ? (
                        <select
                          value={commission.status}
                          onChange={(e) => handleStatusUpdate(commission.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs border border-[var(--border)] rounded-lg px-2 py-1 bg-white text-[var(--foreground)]"
                        >
                          <option value="pending">Pending</option>
                          <option value="under_review">Processing</option>
                          <option value="paid">Paid</option>
                        </select>
                      ) : (
                        <button
                          onClick={() => {
                            setSelectedCommissionForMessaging(commission);
                            setShowMessagingModal(true);
                          }}
                          className="text-xs font-semibold text-[var(--accent)] hover:underline"
                        >
                          Message
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-[var(--border)]">
          {filteredCommissions.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-[var(--muted-foreground)]">
              {commissions.length === 0 ? 'No commissions found' : 'No commissions match your filters'}
            </div>
          ) : filteredCommissions.map(commission => (
            <div key={commission.id} className="px-5 py-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[var(--foreground)] truncate">{commission.guest_first_name} {commission.guest_last_name}</div>
                  <div className="text-xs text-[var(--muted-foreground)] mt-0.5">{commission.supplier}</div>
                </div>
                <StatusBadge status={commission.status} />
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-[var(--muted-foreground)] mb-3">
                <div><span className="font-medium text-[var(--foreground)]">Res:</span> {commission.reservation_number}</div>
                <div><span className="font-medium text-[var(--foreground)]">Travel:</span> {formatDate(commission.travel_date)}</div>
                <div><span className="font-medium text-[var(--foreground)]">{isIndependentAgent ? 'Amount' : 'Gross'}:</span> {formatCurrency(commission.gross_commission)}</div>
                {!isIndependentAgent && (
                  <div><span className="font-medium text-emerald-600">Agent:</span> <span className="text-emerald-600 font-semibold">{formatCurrency(commission.agent_commission)}</span></div>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (commission.status === 'draft') {
                      setSelectedCommission(commission);
                      setShowForm(true);
                    } else {
                      setSelectedCommission(commission);
                    }
                  }}
                  className="flex-1 py-2 border border-[var(--border)] rounded-lg text-xs font-semibold text-[var(--foreground)] hover:bg-[var(--muted)]/50 transition-colors"
                >
                  {commission.status === 'draft' ? 'Edit' : 'View Details'}
                </button>
                {isIndependentAgent ? (
                  <select
                    value={commission.status}
                    onChange={(e) => handleStatusUpdate(commission.id, e.target.value)}
                    className="flex-1 text-xs border border-[var(--border)] rounded-lg px-2 py-2 bg-white text-[var(--foreground)]"
                  >
                    <option value="pending">Pending</option>
                    <option value="under_review">Processing</option>
                    <option value="paid">Paid</option>
                  </select>
                ) : (
                  <button
                    onClick={() => {
                      setSelectedCommissionForMessaging(commission);
                      setShowMessagingModal(true);
                    }}
                    className="flex-1 py-2 border border-[var(--accent)]/30 bg-amber-50 rounded-lg text-xs font-semibold text-[var(--accent)] hover:bg-amber-100 transition-colors"
                  >
                    Message
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        {filteredCommissions.length > 0 && (
          <div className="px-5 py-3 bg-[var(--muted)]/20 border-t border-[var(--border)] flex items-center justify-between">
            <div className="text-xs text-[var(--muted-foreground)]">{filteredCommissions.length} commission{filteredCommissions.length !== 1 ? 's' : ''} shown</div>
            {!isIndependentAgent && (
              <div className="text-xs font-semibold text-[var(--foreground)]">
                Total: <span className="text-emerald-600">{formatCurrency(filteredCommissions.reduce((s, c) => s + parseFloat(c.agent_commission || 0), 0))}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Commission Form Modal */}
      {showForm && (
        <CommissionForm
          onClose={() => {
            setShowForm(false);
            setSelectedCommission(null);
          }}
          onSuccess={handleCommissionSubmit}
          draftCommission={selectedCommission?.status === 'draft' ? selectedCommission : null}
        />
      )}

      {/* Tax Forms Modal - Hidden from independent agents */}
      {showTaxForms && !isIndependentAgent && (
        <TaxForms agentId={user?.id} onClose={() => setShowTaxForms(false)} />
      )}

      {/* Commission Messaging Modal */}
      {showMessagingModal && (
        <CommissionMessagingModal
          commission={selectedCommissionForMessaging}
          onClose={() => setShowMessagingModal(false)}
          onMessageSent={handleMessageSent}
        />
      )}

      {/* Commission Details Modal - Only for non-draft commissions */}
      {selectedCommission && selectedCommission.status !== 'draft' && !showForm && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedCommission(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)] sticky top-0 bg-white rounded-t-2xl z-10">
              <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)]">Commission Details</h2>
              <button onClick={() => setSelectedCommission(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[var(--muted)] text-[var(--muted-foreground)] transition-colors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>

            <div className="px-6 py-5 space-y-6">
              {/* Booking Info */}
              <div>
                <h3 className="text-sm font-semibold text-[var(--foreground)] mb-3">Booking Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-[var(--muted-foreground)] mb-0.5">Reservation Number</div>
                    <div className="text-sm font-medium text-[var(--foreground)] font-mono">{selectedCommission.reservation_number}</div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--muted-foreground)] mb-0.5">Supplier</div>
                    <div className="text-sm font-medium text-[var(--foreground)]">{selectedCommission.supplier}</div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--muted-foreground)] mb-0.5">Booking Date</div>
                    <div className="text-sm text-[var(--foreground)]">{formatDate(selectedCommission.booking_date)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--muted-foreground)] mb-0.5">Travel Date</div>
                    <div className="text-sm text-[var(--foreground)]">{formatDate(selectedCommission.travel_date)}</div>
                  </div>
                </div>
              </div>

              <div className="border-t border-[var(--border)]" />

              {/* Guest Info */}
              <div>
                <h3 className="text-sm font-semibold text-[var(--foreground)] mb-3">Guest Information</h3>
                <div>
                  <div className="text-xs text-[var(--muted-foreground)] mb-0.5">Guest Name</div>
                  <div className="text-sm font-medium text-[var(--foreground)]">{selectedCommission.guest_first_name} {selectedCommission.guest_last_name}</div>
                </div>
              </div>

              <div className="border-t border-[var(--border)]" />

              {/* Commission Details */}
              <div>
                <h3 className="text-sm font-semibold text-[var(--foreground)] mb-3">Commission Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-[var(--muted-foreground)] mb-0.5">Gross Commission</div>
                    <div className="text-sm font-semibold text-[var(--foreground)]">{formatCurrency(selectedCommission.gross_commission)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--muted-foreground)] mb-0.5">Agent Commission</div>
                    <div className="text-base font-bold text-emerald-600">{formatCurrency(selectedCommission.agent_commission)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--muted-foreground)] mb-1">Status</div>
                    <StatusBadge status={selectedCommission.status} />
                  </div>
                  <div>
                    <div className="text-xs text-[var(--muted-foreground)] mb-0.5">Submitted</div>
                    <div className="text-sm text-[var(--foreground)]">{formatDate(selectedCommission.created_at)}</div>
                  </div>
                </div>
              </div>

              {selectedCommission.notes && (
                <div>
                  <h3 className="text-sm font-semibold text-[var(--foreground)] mb-2">Agent Notes</h3>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-[var(--foreground)] whitespace-pre-wrap">
                    {selectedCommission.notes}
                  </div>
                </div>
              )}

              {selectedCommission.admin_notes && (
                <div>
                  <h3 className="text-sm font-semibold text-[var(--foreground)] mb-2">Admin Notes</h3>
                  <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-sm text-amber-900 whitespace-pre-wrap">
                    {selectedCommission.admin_notes}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                {!isIndependentAgent && (
                  <button
                    onClick={() => {
                      setSelectedCommissionForMessaging(selectedCommission);
                      setShowMessagingModal(true);
                      setSelectedCommission(null);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-[var(--border)] rounded-xl text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/50 transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                    Message
                  </button>
                )}
                <button
                  onClick={() => setSelectedCommission(null)}
                  className="flex-1 px-4 py-2.5 bg-[var(--primary)] text-white rounded-xl text-sm font-semibold hover:bg-[var(--primary)]/90 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionsList;
